import {
    Comment,
    commentRelationshipKeys,
    commentRelationshipTypes,
    CommentResource,
} from '@/entities/Comment/Comment'
import { transformCommentFormDataToRequest, transformToComment } from '@/entities/Comment/CommentTransformers'
import { FetchResult, FetchResultType } from '@/entities/FetchResult'
import { NotificationCenterHeaderName, NotificationCenterLog } from '@/entities/NotificationCenter/NotificationCenter'
import { authorizedFetch } from '@/helpers/authorizedFetch'
import { trans } from '@/helpers/trans'
import { generateApiUrl } from '@/helpers/url'
import { isResourceDocument } from '@/japi/guards/isResourceDocument'
import { JapiDocument } from '@/japi/types/Document'

export interface AddCommentFormData {
    comment: string
    relationshipKey: commentRelationshipKeys
    relationshipType: commentRelationshipTypes
    relationshipId: string
    eventLogTrackerData?: NotificationCenterLog
}

const postCommentApiCall = async (addCommentFormData: AddCommentFormData): Promise<FetchResult<Comment, string>> => {
    const commentRequest = transformCommentFormDataToRequest(addCommentFormData)

    try {
        const url = generateApiUrl({
            endpoint: '/comments',
            queryParams: {
                include: 'owner',
            },
        })

        const response = await authorizedFetch(url, {
            method: 'POST',
            body: JSON.stringify({ data: commentRequest }),
            ...addCommentFormData.eventLogTrackerData && {
                headers: {
                    [NotificationCenterHeaderName.X_EVENT]: JSON.stringify(addCommentFormData.eventLogTrackerData),
                },
            },
        })

        const doc: JapiDocument = await response.json()

        if (! isResourceDocument<CommentResource>(doc) || ! doc.data) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: trans('errors.unknownError'),
            }
        }

        const comment = transformToComment(doc.data)

        return {
            status: response.status,
            type: FetchResultType.Success,
            data: comment,
        }
    } catch (error) {
        console.error('[postCommentApiCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}

export {
    postCommentApiCall,
}
